import { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  IconButton,
  MenuItem,
  TableCell,
  TableRow,
  Typography,
  CircularProgress,
} from "@mui/material";
import { format } from "date-fns";
// @types
import { IRecall } from "../../../@types/recalls";
// components
import Iconify from "../../../components/iconify";
import MenuPopover from "../../../components/menu-popover/MenuPopover";
import ConfirmDialog from "../../../components/confirm-dialog/ConfirmDialog";
import Label from "../../../components/label";

// ----------------------------------------------------------------------

type Props = {
  row: IRecall;
  loading: boolean;
  onDeleteRow: VoidFunction;
  onSendRow: VoidFunction;
  onEditRow: VoidFunction;
};

export default function RecallTableRow({
  row,
  loading,
  onDeleteRow,
  onSendRow,
  onEditRow,
}: Props) {
  const {
    patientName,
    communicationType,
    recallType,
    stage,
    status,
    typeOfAppointment,
    lastExamDate,
    recallDate,
  } = row;

  const [openConfirm, setOpenConfirm] = useState(false);
  const [openPopover, setOpenPopover] = useState<HTMLElement | null>(null);

  const handleOpenConfirm = () => {
    setOpenConfirm(true);
  };

  const handleCloseConfirm = () => {
    setOpenConfirm(false);
  };

  const handleOpenPopover = (event: React.MouseEvent<HTMLElement>) => {
    setOpenPopover(event.currentTarget);
  };

  const handleClosePopover = () => {
    setOpenPopover(null);
  };

  const getCommunicationIcon = (type: string) => {
    switch (type) {
      case "SMS":
        return "eva:message-square-fill";
      case "Email":
        return "eva:email-fill";
      case "Phone Call":
        return "eva:phone-fill";
      default:
        return "eva:message-square-fill";
    }
  };

  const getCommunicationColor = (type: string) => {
    switch (type) {
      case "SMS":
        return "info";
      case "Email":
        return "warning";
      case "Phone Call":
        return "success";
      default:
        return "default";
    }
  };

  const getStageColor = (stage: number) => {
    switch (stage) {
      case 1:
        return "success";
      case 2:
        return "warning";
      case 3:
        return "error";
      default:
        return "default";
    }
  };

  const getStatusColor = (statusVal: string | undefined) => {
    if (!statusVal) return "default";
    const s = statusVal.toLowerCase();
    if (s === "scheduled") return "info";
    if (s === "sent") return "success";
    if (s === "completed") return "default";
    if (s === "failed") return "error";
    return "default";
  };

  const formatDate = (dateString: string | undefined) => {
    if (dateString == null || dateString === "") return "-";
    try {
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime()) || date.getTime() === 0) return "-";
      return format(date, "dd/MM/yyyy");
    } catch {
      return "-";
    }
  };

  useEffect(() => {
    if (!loading && openPopover) {
      handleClosePopover();
    }
  }, [loading]);

  return (
    <>
      <TableRow hover>
        <TableCell>
          <Typography variant="subtitle2" noWrap>
            {patientName}
          </Typography>
        </TableCell>

        <TableCell align="center">
          <Typography variant="body2">{communicationType}</Typography>
        </TableCell>

        <TableCell align="center">
          <Typography variant="body2">{recallType}</Typography>
        </TableCell>

        <TableCell align="center">
          <Label
            variant="soft"
            color={getStageColor(stage) as any}
            sx={{ textTransform: "capitalize" }}
          >
            {stage}
          </Label>
        </TableCell>

        <TableCell align="center">
          <Label
            variant="soft"
            color={getStatusColor(status) as any}
            sx={{ textTransform: "capitalize" }}
          >
            {status || "Scheduled"}
          </Label>
        </TableCell>

        <TableCell align="center">
          <Typography variant="body2">{typeOfAppointment}</Typography>
        </TableCell>

        <TableCell align="center">
          <Typography variant="body2">{formatDate(lastExamDate)}</Typography>
        </TableCell>

        <TableCell align="center">
          <Typography variant="body2">{formatDate(recallDate)}</Typography>
        </TableCell>

        <TableCell align="center">
          <IconButton color="default" onClick={handleOpenPopover}>
            <Iconify icon="eva:more-vertical-fill" />
          </IconButton>
        </TableCell>
      </TableRow>

      <MenuPopover
        open={openPopover}
        onClose={handleClosePopover}
        arrow="right-top"
        sx={{ width: 140 }}
      >
        <MenuItem
          onClick={() => {
            if (!loading) {
              onSendRow();
            }
          }}
          sx={{ color: "success.main" }}
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={16} />
          ) : (
            <Iconify icon="eva:paper-plane-fill" />
          )}
          <Typography >
            {loading ? "Sending..." : "Send Now"}
          </Typography>
          
        </MenuItem>

        <MenuItem
          onClick={() => {
            handleOpenConfirm();
            handleClosePopover();
          }}
          sx={{ color: "error.main" }}
        >
          <Iconify icon="eva:trash-2-outline" />
          Delete
        </MenuItem>

        <MenuItem
          onClick={() => {
            onEditRow();
            handleClosePopover();
          }}
        >
          <Iconify icon="eva:edit-fill" />
          Edit
        </MenuItem>
      </MenuPopover>

      <ConfirmDialog
        open={openConfirm}
        onClose={handleCloseConfirm}
        title="Delete"
        content="Are you sure want to delete?"
        action={
          <Button variant="contained" color="error" onClick={onDeleteRow}>
            Delete
          </Button>
        }
      />
    </>
  );
}
